<template>
<PageSection>
    <PageHeader id="page-header" title="Course Data Files"></PageHeader>

    <!-- Loading State -->
    <div v-if="isLoadingStore && !storeData && !storeDetailError" class="flex items-center justify-center py-20">
        <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Loading store information...</p>
        </div>
    </div>

    <!-- No Store State -->
    <div v-else-if="!storeData && (!storeDetailError || storeDetailError.message?.includes('not found'))" class="bg-white border rounded-lg shadow-sm p-8">
        <div class="text-center">
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Store Not Configured</h2>
            <p class="text-gray-600 mb-6">Please configure your store first before managing course files.</p>
            <router-link to="/store/configuration" class="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                Go to Store Configuration
            </router-link>
        </div>
    </div>

    <!-- Error State -->
    <div v-else-if="!storeData && storeDetailError && !storeDetailError.message?.includes('not found')" class="flex items-center justify-center py-20">
        <div class="text-center max-w-md">
            <h2 class="text-xl font-semibold text-red-600 mb-4">Error Loading Store</h2>
            <p class="text-gray-600 mb-6">{{ storeDetailError.message || 'Failed to load store details. Please try again.' }}</p>
            <button @click="retryLoadStore" class="px-6 py-3 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700">
                Retry
            </button>
        </div>
    </div>

    <!-- Main Content -->
    <CourseImports v-else-if="storeData" />
</PageSection>
</template>

<script lang="ts" setup>
import { useQuery, useQueryCache } from "@pinia/colada";
import { definePage } from "vue-router/auto";
import CourseImports from "../../components/Store/CourseImports.vue";
import { useToast } from "../../composables/useToast";
import { trpc } from "../../trpc/client";

definePage({
	meta: {
		requiresAuth: true,
		featureFlag: "store-management",
	},
});

const {
	isLoading: isLoadingStore,
	error: storeDetailError,
	data: storeData,
	refetch: refetchStoreData,
} = useQuery({
	key: ["store"],
	query: () => trpc.storeManagement.getStoreDetails.query({}),
	gcTime: 1000 * 60 * 10,
	staleTime: 1000 * 60 * 5,
});

const { showErrorToast, showSuccessToast } = useToast();
const storeQueryCache = useQueryCache();

const retryLoadStore = async () => {
	try {
		storeQueryCache.invalidateQueries({
			predicate: (query) =>
				Array.isArray(query.key) && query.key[0] === "store",
		});
		await refetchStoreData();
		showSuccessToast("Store data refreshed successfully");
	} catch (error) {
		showErrorToast("Failed to reload store data. Please try again.");
	}
};
</script>
